import { useMemo, useRef, useState } from 'react';
import { formatShortDate } from '@/lib/format';
import { smoothPath } from '@/lib/smoothPath';

interface Props {
  days: { date: string; count: number }[];
  height?: number;
}

// Smooth filled area chart. No chart library — a single SVG path + linear
// gradient, matching the sparkline language used across the rest of the
// dashboard. Path math lives in lib/smoothPath so the Wall sparklines share it.
export default function Heartbeat({ days, height = 96 }: Props) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hover, setHover] = useState<number | null>(null);

  const VW = 1000;    // virtual viewBox width — preserveAspectRatio stretches it
  const VH = 100;
  const PAD = 4;

  const { max, area, line, yFor, avgLine } = useMemo(() => {
    if (days.length === 0) return { max: 0, area: '', line: '', yFor: () => 0, avgLine: '' };
    const max = Math.max(1, ...days.map((d) => d.count));
    const innerH = VH - PAD * 2;
    const { line, area } = smoothPath(days.map((d) => d.count), { width: VW, height: VH, pad: PAD });
    const avg = days.reduce((a, b) => a + b.count, 0) / days.length;
    const avgY = PAD + innerH - (avg / max) * innerH;
    const avgLine = `M 0,${avgY} L ${VW},${avgY}`;
    const yFor = (count: number) => PAD + innerH - (count / max) * innerH;
    return { max, area, line, yFor, avgLine };
  }, [days]);

  if (days.length === 0) {
    return <div className="h-24 flex items-center justify-center text-[12px] text-fg-subtle">No activity</div>;
  }

  const hoverDay = hover != null ? days[hover] : null;
  const total = days.reduce((a, b) => a + b.count, 0);
  const avg = Math.round(total / days.length);

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const rel = (e.clientX - rect.left) / rect.width;
    const idx = Math.min(days.length - 1, Math.max(0, Math.round(rel * (days.length - 1))));
    setHover(idx);
  }

  return (
    <div className="relative">
      {/* small summary strip above the chart */}
      <div className="flex items-baseline justify-between text-[11px] text-fg-subtle mb-2">
        <div>
          <span className="num text-fg font-semibold">{total.toLocaleString()}</span>
          <span className="ml-1.5">commits in the last {days.length} days</span>
        </div>
        <div>
          avg <span className="num text-fg-muted font-medium">{avg}</span>/day |
          peak <span className="num text-fg-muted font-medium ml-1">{max}</span>
        </div>
      </div>

      <svg
        ref={svgRef}
        viewBox="0 0 1000 100"
        preserveAspectRatio="none"
        className="block w-full"
        style={{ height }}
        onMouseMove={handleMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="hb-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="currentColor" stopOpacity="0.28" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* subtle average baseline */}
        <path d={avgLine} stroke="currentColor" strokeOpacity="0.12" strokeWidth="1"
          strokeDasharray="2 4" vectorEffect="non-scaling-stroke" className="text-fg-subtle" />

        <path d={area} fill="url(#hb-fill)" className="text-accent-500" />
        <path d={line} fill="none" stroke="currentColor" strokeWidth="1.5"
          strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke"
          className="text-accent-500" />

        {hover != null && (
          <>
            <line x1={(hover / Math.max(1, days.length - 1)) * 1000}
                  x2={(hover / Math.max(1, days.length - 1)) * 1000}
                  y1={0} y2={100}
                  stroke="currentColor" strokeOpacity="0.25" strokeWidth="1"
                  vectorEffect="non-scaling-stroke" className="text-fg" />
            <circle cx={(hover / Math.max(1, days.length - 1)) * 1000}
                    cy={yFor(days[hover].count)}
                    r="1.25"
                    fill="currentColor"
                    vectorEffect="non-scaling-size"
                    className="text-accent-500">
              <title>{days[hover].date}: {days[hover].count} commits</title>
            </circle>
          </>
        )}
      </svg>

      {/* hover tooltip — positioned absolutely over the SVG */}
      {hoverDay && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full top-8 px-2 py-1 rounded-md bg-bg-elev border border-fg/10 shadow-md text-[11px] whitespace-nowrap"
          style={{ left: `${(hover! / Math.max(1, days.length - 1)) * 100}%` }}
        >
          <div className="num text-fg font-semibold">{hoverDay.count} commits</div>
          <div className="text-fg-subtle num">{formatShortDate(hoverDay.date)}</div>
        </div>
      )}

      <div className="flex items-center justify-between text-[10px] text-fg-subtle mt-2 num">
        <span>{formatShortDate(days[0].date)}</span>
        <span>{formatShortDate(days[days.length - 1].date)}</span>
      </div>
    </div>
  );
}
